import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { wallKeys, wallBackgroundMap, cardColors, getFamewallBrandingUrl } from '@/src/constants/wall.constant';

import CarouselSlider from '@/src/components/carousel/CarouselSlider';
import ClassNames from '@/src/components/componentHelper/ClassNames';
import GetFontSelectorComponent from '@/src/components/utilities/GetFontSelectorComponent';
import LogAnalytics from '@/src/components/utilities/TinyBirdLog';
import ReviewSummaryComponent from '@/src/components/review-summary/ReviewSummaryComponent';

import EmbedLoader from '../lib/EmbedLoader';
import { fetchSeoEmbed } from '../lib/api';
import { getEmbedOrigin } from '../lib/origin';

// Faithful no-iframe port of pages/carousel/[wallUrl].js.
export default function SeoCarouselView({ initialData, config, apiOrigin }) {
  const cardItems = initialData.results;
  const wall = initialData.wall;
  const url = config.wallUrl;

  const [render, setRender] = useState(false);
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [selectedCategory, setActiveCategory] = useState(null);
  const [localCardItems, setLocalCardItems] = useState(cardItems || []);

  const [backgroundImage, setBackgroundImage] = useState('transparent');
  const [smallLogo, setSmallLogo] = useState(false);
  const [compactSpacing, setCompactSpacing] = useState(false);

  useEffect(() => {
    setLocalCardItems(cardItems || []);
    try {
      if (wall && wall[wallKeys.categories] && wall[wallKeys.categories].length > 0) {
        setActiveCategory(wall[wallKeys.categories][0]);
      }
    } catch (err) {}
    setRender(true);
  }, []);

  const isMobile = useMediaQuery({ maxWidth: 634 });

  useEffect(() => {
    if (isMobile) {
      setSmallLogo(true);
    } else {
      setSmallLogo(false);
    }
  }, [isMobile]);

  const loadFilteredItems = async (params = {}) => {
    const cardParams = { ...params, wall_url: url };
    const payload = await fetchSeoEmbed(apiOrigin, cardParams);
    if (payload.status) {
      setLocalCardItems(payload.results);
    }
  };

  const changeActiveCategory = async (category) => {
    if (category === selectedCategory) {
      return;
    }

    setActiveCategory(category);
    setIsCategoryLoading(true);
    await loadFilteredItems({ category });
    setIsCategoryLoading(false);
  };

  useEffect(() => {
    if (wall && (wall[wallKeys.backgroundColor] || wall[wallKeys.backgroundColor] === 0)) {
      const wallColor = wallBackgroundMap(wall[wallKeys.backgroundColor]);
      setBackgroundImage(wallColor);
    }

    const theme = config.theme;

    let preempt = false;
    let cardBackgroundObject = {};

    if (theme) {
      if (theme === 'dark') {
        if (wall) {
          wall[wallKeys.isDarkModeEnabled] = true;
          cardBackgroundObject = { backgroundColor: 'rgba(51,54,58,1)', backgroundImage: 'none' };
          preempt = true;
        }
      } else if (theme === 'light') {
        if (wall) {
          wall[wallKeys.isDarkModeEnabled] = false;
          cardBackgroundObject = { backgroundColor: cardColors.light, backgroundImage: 'none' };
          preempt = true;
        }
      } else {
        if (wall && wall[wallKeys.isDarkModeEnabled]) {
        }
      }
    } else {
      if (wall && wall[wallKeys.isDarkModeEnabled]) {
      }
    }

    if (wall && wall[wallKeys.cardBackgroundColor] && !preempt) {
      cardBackgroundObject = { backgroundColor: wall[wallKeys.cardBackgroundColor], backgroundImage: 'none' };
    } else if (!preempt) {
      if (wall && wall[wallKeys.isDarkModeEnabled]) {
        if (wall && wall[wallKeys.darkCardTheme] === 1) {
          cardBackgroundObject = { backgroundColor: 'rgba(51,54,58,1)', backgroundImage: 'none' };
        } else {
          cardBackgroundObject = { backgroundImage: cardColors.dark };
        }
      } else {
        cardBackgroundObject = { backgroundImage: cardColors.light };
      }
    }

    if (wall) {
      wall[wallKeys.cardBackgroundObject] = cardBackgroundObject;
    }

    const carouselAutoplay = config.autoplay;
    if (carouselAutoplay === '1') {
      if (wall) {
        wall[wallKeys.carouselAutoplay] = true;
      }
    }

    const spacingParam = config.spacing;
    if (spacingParam === 'true' || spacingParam === '1') {
      setCompactSpacing(true);
    } else {
      setCompactSpacing(false);
    }

    if (wall) {
      LogAnalytics(wall);
    }
  }, []);

  if (!render || !wall) {
    return <EmbedLoader />;
  }

  return (
    <>
      {wall && wall[wallKeys.fontStyle] && wall[wallKeys.fontStyle] !== ''
        ? GetFontSelectorComponent(wall[wallKeys.fontStyle])
        : null}

      <div className={ClassNames(wall[wallKeys.isEnableBannerReview] ? '' : 'pt-5', 'h-full pb-5')} style={{ backgroundImage: backgroundImage, backgroundRepeat: 'no-repeat', borderBottomRightRadius: '1px' }}>

        {wall && wall[wallKeys.isEnableReviewSummary] && (
          <ReviewSummaryComponent
            reviewSummaryText={wall[wallKeys.reviewSummaryText]}
            reviewSummaryKeyThemes={wall[wallKeys.reviewSummaryKeyThemes]}
            reviewSummaryTotalTestimonials={wall[wallKeys.reviewSummaryTotalTestimonials]}
            isLoadingSummary={false}
            isReviewSummaryEnabled={wall[wallKeys.isEnableReviewSummary]}
            fontStyle={wall[wallKeys.fontStyle]}
            isDarkModeEnabled={typeof wall[wallKeys.isReviewSummaryDarkModeEnabled] !== 'undefined' ? wall[wallKeys.isReviewSummaryDarkModeEnabled] : false}
          />
        )}

        {wall[wallKeys.isEnableBannerReview] ? (
          <div className={`ml-1 justify-${wall[wallKeys.alignBanner]}`} style={{ paddingTop: '10px', display: 'flex', marginBottom: 10, transition: 'all 0.3s ease 0s', marginLeft: '40px' }}>
            <div
              className={ClassNames(wall[wallKeys.isDarkModeEnabled] ? 'border border-gray-100' : 'border border-gray-400', 'text-base font-medium flex py-2 px-4 shadow-lg')}
              style={
                wall[wallKeys.isDarkModeEnabled]
                  ? { backgroundImage: 'linear-gradient(-50deg,rgba(0,0,0,.94),rgba(0,0,0,.58) 100%)', boxSizing: 'border-box', textAlign: 'center', outline: 'none', borderRadius: '20px', transition: 'all 0.3s ease 0s' }
                  : { backgroundImage: 'linear-gradient(-50deg,rgba(255,255,255,.5),rgba(255,255,255,.95) 80%)', backgroundColor: 'white', boxSizing: 'border-box', textAlign: 'center', outline: 'none', borderRadius: '20px', transition: 'all 0.3s ease 0s' }
              }
            >
              {wall[wallKeys.isEnableBannerReview] ? (
                <>
                  <ul className="flex justify-center mr-2" style={{ marginTop: 5 }}>
                    {[...Array(Math.floor((wall[wallKeys.averageRating] && wall[wallKeys.averageRating] > 0) ? wall[wallKeys.averageRating] : 5))].map((e, i) => (
                      <li key={e}>
                        <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="star" className="w-4 text-yellow-500" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
                          <path fill="currentColor" d="M259.3 17.8L194 150.2 47.9 171.5c-26.2 3.8-36.7 36.1-17.7 54.6l105.7 103-25 145.5c-4.5 26.3 23.2 46 46.4 33.7L288 439.6l130.7 68.7c23.2 12.2 50.9-7.4 46.4-33.7l-25-145.5 105.7-103c19-18.5 8.5-50.8-17.7-54.6L382 150.2 316.7 17.8c-11.7-23.6-45.6-23.9-57.4 0z" />
                        </svg>
                      </li>
                    ))}
                    {wall[wallKeys.averageRating] && wall[wallKeys.averageRating] % 1 === 0.5 && (
                      <li>
                        <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="star-half-alt" className="w-4 text-yellow-500" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 536 512">
                          <path fill="currentColor" d="M508.55 171.51L362.18 150.2 296.77 17.81C290.89 5.98 279.42 0 267.95 0c-11.4 0-22.79 5.9-28.69 17.81l-65.43 132.38-146.38 21.29c-26.25 3.8-36.77 36.09-17.74 54.59l105.89 103-25.06 145.48C86.98 495.33 103.57 512 122.15 512c4.93 0 10-1.17 14.87-3.75l130.95-68.68 130.94 68.7c4.86 2.55 9.92 3.71 14.83 3.71 18.6 0 35.22-16.61 31.66-37.4l-25.03-145.49 105.91-102.98c19.04-18.5 8.52-50.8-17.73-54.6zm-121.74 123.2l-18.12 17.62 4.28 24.88 19.52 113.45-102.13-53.59-22.38-11.74.03-317.19 51.03 103.29 11.18 22.63 25.01 3.64 114.23 16.63-82.65 80.38z" />
                        </svg>
                      </li>
                    )}
                  </ul>
                  <div style={wall[wallKeys.isDarkModeEnabled] ? { color: 'hsla(0, 0%, 100%, 0.8)' } : { color: '#111' }}>{(wall[wallKeys.bannerReviewText] && wall[wallKeys.bannerReviewText] !== '') ? wall[wallKeys.bannerReviewText] : ''}</div>
                </>
              ) : null}
            </div>
          </div>
        ) : null}

        {wall[wallKeys.categories] && wall[wallKeys.categories].length > 0 && (
          <div className="flex flex-wrap justify-center gap-3 py-3">
            {wall[wallKeys.categories].map((category) => (
              <button key={category} disabled={isCategoryLoading} onClick={() => changeActiveCategory(category)} className={`inline-flex text-base font-semibold py-2 px-3 mt-1 rounded-md cursor-pointer ${wall[wallKeys.fontStyle] ? `font-${wall[wallKeys.fontStyle]}` : ''}`} style={(selectedCategory && selectedCategory === category) ? { color: wall[wallKeys.activeCategoryTextColor], backgroundColor: wall[wallKeys.activeCategoryBackgroundColor] } : { color: wall[wallKeys.categoryTextColor], backgroundColor: wall[wallKeys.categoryBackgroundColor] }}>
                {(isCategoryLoading && category === selectedCategory) ? (
                  <svg aria-hidden="true" className="w-5 h-5 fill-blue-200 animate-spin" style={(selectedCategory && selectedCategory === category) ? { color: wall[wallKeys.activeCategoryTextColor] } : { color: wall[wallKeys.categoryTextColor] }} viewBox="0 0 100 101" fill={(selectedCategory && selectedCategory === category) ? wall[wallKeys.activeCategoryBackgroundColor] : wall[wallKeys.categoryBackgroundColor]} xmlns="http://www.w3.org/2000/svg">
                    <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                    <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                  </svg>
                ) : category}
              </button>
            ))}
          </div>
        )}

        <main className="mx-auto">
          <div className="h-full w-full">
            <div className="relative">
              <div className="relative  mobile-scaling w-full">
                <div className="rounded-lg w-full h-full relative content-shadow overflow-hidden">
                  <div id="content" className=" content-container transition-colors h-full w-full" style={{ fontSize: 16, maxWidth: '100%' }}>

                    {localCardItems ? (
                      <CarouselSlider
                        cardItems={localCardItems}
                        wall={wall}
                        isMobile={isMobile}
                        compactSpacing={compactSpacing}
                      />
                    ) : null}

                    {!wall || (wall && wall[wallKeys.isLogoDisabled]) ? null : (
                      <div className="flex justify-center text-center items-center">
                        <a href={getFamewallBrandingUrl(wall, 'widget')} target="_blank" rel="noreferrer" className="flex items-center px-2 duration-100 rounded-md no-underline" style={{ textDecoration: 'none' }}>
                          <div className="font-semibold text-gray-600 no-underline whitespace-nowrap" style={{ fontSize: '17px', marginRight: '11px' }}>Made with</div>
                          <Image src={`${getEmbedOrigin()}/DarkFamewallLogo.svg`} placeholder="blur" blurDataURL={`${getEmbedOrigin()}/DarkFamewallLogo.svg`} alt="Famewall Logo" loading="lazy" width="130px" height="50px" />
                        </a>
                      </div>
                    )}

                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}
